import * as React from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../utils/cn'
import { Loader2 } from 'lucide-react'

const buttonVariants = cva(
  "group relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border font-medium text-sm transition duration-300 disabled:pointer-events-none disabled:opacity-50 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 touch-manipulation motion-reduce:transition-none motion-reduce:transform-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 active:scale-[0.97]",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground border border-primary/10 shadow-sm hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]',
        primary:
          'bg-primary text-primary-foreground font-medium shadow-sm hover:shadow-md hover:bg-primary/90 active:scale-[0.98] transition duration-150',
        secondary:
          'bg-white dark:bg-white/5 text-foreground border border-border shadow-soft hover:bg-muted dark:hover:bg-white/10 hover:shadow-hover hover:-translate-y-0.5',
        soft: 'bg-white dark:bg-white/5 text-foreground border-transparent shadow-soft hover:bg-muted dark:hover:bg-white/10 hover:shadow-hover hover:-translate-y-0.5',
        subtle:
          'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 border-transparent hover:bg-slate-200 dark:hover:bg-slate-700',
        ghost:
          'bg-transparent text-muted-foreground border-transparent hover:bg-secondary/50 hover:text-foreground',
        outline:
          'bg-transparent border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-400 transition duration-150',
        destructive:
          'bg-destructive text-white border-transparent shadow-sm hover:bg-destructive/90',
        danger: 'bg-red-600 text-white border-transparent shadow-sm hover:bg-red-700',
        success:
          'bg-emerald-500 text-white font-medium shadow-sm hover:shadow-md hover:bg-emerald-600 active:scale-[0.98] transition duration-150',
        glass:
          'glass text-foreground border-black/5 dark:border-white/20 shadow-sm hover:bg-white/80 dark:hover:bg-white/10 hover:shadow-md hover:-translate-y-0.5 backdrop-blur-xl',
        'ghost-glass':
          'glass border border-white/10 bg-white/5 text-foreground hover:bg-white/10 hover:border-white/20 dark:bg-white/5 dark:hover:bg-white/10',
        linear:
          'bg-gradient-to-b from-white to-muted/20 text-foreground border border-border shadow-sm hover:bg-muted/30 dark:from-slate-800 dark:to-slate-900 dark:text-white dark:border-slate-700 hover:scale-[1.01]',
        white:
          'bg-white text-primary border-transparent hover:bg-white/90 shadow-sm hover:shadow-md',
        link: 'text-primary border-transparent underline-offset-4 hover:underline p-0 h-auto',
        'whatsapp-ghost':
          'bg-emerald-50 text-emerald-700 border border-emerald-200/50 hover:bg-emerald-100 hover:border-emerald-300 font-medium px-4 py-2 flex items-center gap-2 transition',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3.5 text-xs',
        md: 'h-10 px-5',
        lg: 'h-12 px-7 text-base',
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-8 w-8 p-0',
        'icon-lg': 'h-12 w-12 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

type ButtonBaseProps = Omit<React.ComponentProps<'button'>, 'children'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    icon?: React.ReactNode
    iconLabel?: string
    iconPosition?: 'left' | 'right'
    fullWidth?: boolean
  }

type ButtonWithChildren = ButtonBaseProps & {
  children: React.ReactNode
}

type ButtonIconOnly = ButtonBaseProps & {
  children?: undefined
} & ({ 'aria-label': string } | { 'aria-labelledby': string } | { iconLabel: string })

export type ButtonProps = ButtonWithChildren | ButtonIconOnly

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      icon,
      iconLabel,
      iconPosition = 'left',
      fullWidth = false,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = loading || disabled
    const isIconOnly = React.Children.count(children) === 0
    const computedAriaLabel =
      isIconOnly && !props['aria-label'] && !props['aria-labelledby']
        ? (iconLabel ?? (typeof props.title === 'string' ? props.title : undefined))
        : undefined
    const resolvedTabIndex = isDisabled && asChild ? -1 : props.tabIndex

    const iconElement = icon && (
      <span
        className={cn(
          'inline-flex shrink-0 items-center text-[1.1em] transition-transform',
          iconPosition === 'right' && 'group-hover:translate-x-0.5',
          iconPosition === 'left' && 'group-hover:scale-110'
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
    )

    const loadingOverlay = loading && (
      <span
        className="absolute inset-0 flex items-center justify-center rounded-[inherit] bg-inherit"
        aria-hidden="true"
      >
        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
      </span>
    )

    const label = children ? (
      <span
        className={cn(loading && 'invisible', 'inline-flex min-w-0 items-center gap-1 truncate')}
      >
        {children}
      </span>
    ) : null

    const slottableElement = React.isValidElement(children)
      ? (children as React.ReactElement<{ children?: React.ReactNode }>)
      : null
    const isSlottableChild = asChild && slottableElement !== null
    const slottableChild = isSlottableChild
      ? React.cloneElement(
          slottableElement,
          undefined,
          slottableElement.props.children ? (
            <span
              className={cn(
                loading && 'invisible',
                'inline-flex min-w-0 items-center gap-1 truncate'
              )}
            >
              {slottableElement.props.children}
            </span>
          ) : null
        )
      : children

    return (
      <Comp
        data-slot="button"
        {...props}
        type={asChild ? undefined : type}
        className={cn(
          buttonVariants({ variant, size }),
          fullWidth && 'w-full',
          loading && 'pointer-events-none',
          className
        )}
        disabled={asChild ? undefined : isDisabled}
        data-disabled={isDisabled ? 'true' : undefined}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        aria-label={computedAriaLabel ?? props['aria-label']}
        tabIndex={resolvedTabIndex}
        ref={ref}
      >
        {loadingOverlay}
        {!loading && icon && iconPosition === 'left' && iconElement}
        {isSlottableChild ? <Slottable>{slottableChild}</Slottable> : label}
        {!loading && icon && iconPosition === 'right' && iconElement}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { buttonVariants }
